/**
 * Where one burst ends and the next begins.
 * Pure module: no imports, no I/O.
 *
 * A burst is a run of frames of the same moment — the forty shots of a heron
 * taking off that a photographer fires in four seconds. Consecutive frames are
 * compared by cosine similarity of their embeddings; the run continues while
 * they stay alike and breaks when they do not.
 */

export interface Framed {
  id: number;
  /** Unit-length embedding. Capture order is the order of this array. */
  vector: Float32Array;
}

/**
 * How alike two frames must be to belong to the same burst.
 *
 * Measured on the bundled shoot with DINOv2-small, not guessed:
 *
 *   consecutive different photographs   0.0638 – 0.5261  (median 0.2627)
 *   the same scene, moved 4–90px,
 *   rotated up to 4°, rescaled 8%,
 *   re-exposed 15%                      0.9658 – 1.0000
 *
 * 0.92 sits in the empty middle of that gap, far from both edges. The upper
 * figures come from transforming one photograph rather than from a real burst,
 * so they are an upper bound: a bird that actually moves its wings between
 * frames will score lower than a bird that has merely been nudged across the
 * canvas. That is the direction this number will need tuning, on real bursts,
 * and it is why it lives in a named constant in a pure module.
 */
const SAME_BURST = 0.92;

/**
 * How alike a frame must stay to the frame that opened its burst.
 *
 * Without this, bursts chain: a bird walking slowly across a field gives two
 * hundred frames where every neighbouring pair is nearly identical and the ends
 * have nothing in common. That is not one moment, and calling it one would put
 * four picks on two hundred frames and quietly hide the rest.
 *
 * Looser than SAME_BURST on purpose — it is a guard against drift, not a second
 * opinion about neighbours.
 */
const SAME_MOMENT = 0.85;

/**
 * Groups frames into bursts, in capture order.
 *
 * Returns every frame's burst id, singletons included, so callers never have to
 * decide what an absent id means. Ids are sequential from 1 in capture order.
 */
export function burstsOf(
  frames: Framed[],
  thresholds: { neighbour?: number; anchor?: number } = {},
): Map<number, number> {
  const neighbour = thresholds.neighbour ?? SAME_BURST;
  const anchor = thresholds.anchor ?? SAME_MOMENT;

  const bursts = new Map<number, number>();
  if (frames.length === 0) return bursts;

  let burstId = 1;
  let first = frames[0];
  bursts.set(first.id, burstId);

  for (let i = 1; i < frames.length; i++) {
    const previous = frames[i - 1];
    const frame = frames[i];

    const continues =
      cosine(previous.vector, frame.vector) >= neighbour &&
      cosine(first.vector, frame.vector) >= anchor;

    if (!continues) {
      burstId++;
      first = frame;
    }
    bursts.set(frame.id, burstId);
  }

  return bursts;
}

/** Groups ids by burst, preserving capture order within each group. */
export function groupByBurst(bursts: Map<number, number>): Map<number, number[]> {
  const groups = new Map<number, number[]>();
  for (const [mediaId, burstId] of bursts) {
    const group = groups.get(burstId);
    if (group) group.push(mediaId);
    else groups.set(burstId, [mediaId]);
  }
  return groups;
}

/**
 * Cosine similarity of two vectors.
 *
 * Divides by the norms rather than assuming unit length. The model returns
 * normalised vectors today; a different model, or a rounding loss on the way
 * through SQLite, should degrade the number rather than silently invalidate
 * every threshold in this file.
 */
export function cosine(a: Float32Array, b: Float32Array): number {
  const length = Math.min(a.length, b.length);
  let dot = 0, normA = 0, normB = 0;

  for (let i = 0; i < length; i++) {
    dot += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }

  const scale = Math.sqrt(normA) * Math.sqrt(normB);
  return scale === 0 ? 0 : dot / scale;
}

export const BURST_THRESHOLDS = { SAME_BURST, SAME_MOMENT };
